// „Плащане на доставки" — неплатените документи по доставчик (без период):
// отметка на цялата група (три състояния) или на отделен документ, лепкав
// футър „Избрани N · сума" и „Отбележи като платени" → модал „Плащане"; тостът
// след плащането има „Отмени" (unpay на същите документи).
// Клавиатура: ↑/↓ по редовете, Space отмята, Ctrl/Cmd+Enter отваря плащането.
// Плащане на фактура покрива и обобщените ѝ стокови (BE); стоковите, платени
// отделно, не са в сумата ѝ („N стокови са платени отделно"). Плътни редове на
// един ред (36px, на пръст ≥44px): вторичното — приглушено на същия ред, с
// бележката на документа. Горе — общата лента без период (Excel на групите със
// сумите им — `deliveryPaymentsModel` · Опресни).
import { useMemo, useRef, useState } from "react";
import { App, ConfigProvider, Spin } from "antd";
import { CheckOutlined, CloudServerOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useIsDarkTheme } from "../../../store/hooks";
import { useViewport } from "../../../hooks/useViewport";
import {
  usePayableDeliveries,
  usePayDeliveries,
  useUnpayDeliveries,
} from "../../../hooks/useInventoryDeliveries";
import { exportToExcel } from "../../../utils/excelExport";
import { hasOpenOverlay } from "../../../lib/overlay";
import { PageHeader } from "../components/list/PageHeader";
import { ReportToolbar } from "../components/list/ReportToolbar";
import { refreshOf } from "../components/list/reportRefresh";
import { EmptyScreen } from "../components/list/ListBits";
import { PRIMARY_BUTTON_CLASS } from "../components/list/buttonStyles";
import { ROW_DENSE } from "../components/list/density";
import { CheckBox } from "../components/filters/CheckBox";
import { KbdChip } from "../components/KbdChip";
import { formatMoney } from "../components/numbers";
import { useActionToast } from "../components/useActionToast";
import { DeliveryTypeBadge } from "../delivery/components/DeliveryBadges";
import { OVERDUE_PAYMENT_DAYS } from "../delivery/components/deliveryMeta";
import { PayDeliveriesModal } from "./components/PayDeliveriesModal";
import {
  groupPayableBySupplier,
  payableAgeDays,
  paymentExcelColumns,
  paymentExcelLabels,
  paymentExcelRows,
} from "./deliveryPaymentsModel";

export const DeliveryPaymentsPage = () => {
  const { t } = useTranslation();
  const isDark = useIsDarkTheme();
  const { message } = App.useApp();
  const { isNarrow } = useViewport();
  const showToast = useActionToast();
  const payableQuery = usePayableDeliveries();
  const { data: payable = [], isLoading, isError } = payableQuery;
  const { refresh, refreshing } = refreshOf(payableQuery);
  const payMutation = usePayDeliveries();
  const unpayMutation = useUnpayDeliveries();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [payOpen, setPayOpen] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);

  // „Без доставчик" най-отдолу, останалите по азбучен ред
  const groups = useMemo(
    () => groupPayableBySupplier(payable, t("inventoryPage.homeNoSupplier")),
    [payable, t],
  );

  const selectedDocs = payable.filter((doc) => selected.has(doc.id));
  const selectedTotal = selectedDocs.reduce((sum, doc) => sum + (doc.totalValueWithVat || 0), 0);
  const unpaidTotal = payable.reduce((sum, doc) => sum + (doc.totalValueWithVat || 0), 0);

  const toggle = (ids: number[], on: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });

  const openPay = () => {
    if (!selectedDocs.length) {
      message.info(t("inventoryPage.payPickFirst"));
      return;
    }
    setPayOpen(true);
  };

  // Бързата поправка на току-що сгрешено: „Отмени" в тоста връща същите в неплатени
  const undoPay = (ids: number[]) =>
    unpayMutation.mutate(ids, {
      onSuccess: () => showToast({ text: t("inventoryPage.payUndoneToast", { count: ids.length }) }),
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { detail?: string } } };
        message.error(err.response?.data?.detail || t("inventoryPage.payUnpayError"));
      },
    });

  const confirmPay = (payment: { paidAt: dayjs.Dayjs; method: "cash" | "bank" | "card"; reference: string | null }) => {
    const ids = selectedDocs.map((doc) => doc.id);
    payMutation.mutate(
      {
        ids,
        paidAt: payment.paidAt.toISOString(),
        method: payment.method,
        reference: payment.reference,
      },
      {
        onSuccess: () => {
          showToast({
            text: t("inventoryPage.payDoneToast", { count: ids.length }),
            actionLabel: t("inventoryPage.payUndo"),
            onAction: () => undoPay(ids),
          });
          setPayOpen(false);
          setSelected(new Set());
        },
        onError: (e: unknown) => {
          const err = e as { response?: { data?: { detail?: string } } };
          message.error(err.response?.data?.detail || t("inventoryPage.payError"));
        },
      },
    );
  };

  // Всички неплатени, групирани както на екрана, с реда „Общо" на всяка група
  const exportXls = () =>
    exportToExcel({
      filename: "plashtane-na-dostavki",
      sheetName: t("inventoryPage.navDeliveryPayments"),
      rows: paymentExcelRows(groups, paymentExcelLabels(t)),
      columns: paymentExcelColumns(t),
    });

  // ↑/↓ по редовете (заглавия на групи и документи), Ctrl+Enter = плащане
  const onKeyDown = (event: React.KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
      if (hasOpenOverlay()) return;
      event.preventDefault();
      openPay();
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    const rows = Array.from(listRef.current?.querySelectorAll<HTMLElement>("[data-pay-row]") ?? []);
    const index = rows.indexOf(document.activeElement as HTMLElement);
    const next = event.key === "ArrowDown" ? index + 1 : index - 1;
    if (next < 0 || next >= rows.length) return;
    event.preventDefault();
    rows[next].focus();
  };

  // Плътен ред (общият `ROW_DENSE`): един ред, 34px с мишка, ≥44px на пръст
  const rowClass = `flex w-full cursor-pointer items-center gap-3 border-none px-[18px] text-left text-ink outline-none focus-visible:shadow-[inset_0_0_0_2px_var(--color-brand)] ${ROW_DENSE}`;

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#D97706",
          colorPrimaryHover: "#B45309",
          controlItemBgActive: isDark ? "#382a12" : "#FFF4E0",
          borderRadius: 8,
        },
      }}
    >
      <div className="flex h-full flex-col bg-canvas text-ink" onKeyDown={onKeyDown}>
        <PageHeader
          title={t("inventoryPage.navDeliveryPayments")}
          subtitle={
            payable.length
              ? `${t("inventoryPage.homeUnpaidCount", { count: payable.length })} · ${formatMoney(unpaidTotal)}`
              : undefined
          }
          right={
            <ReportToolbar
              onExport={exportXls}
              exportDisabled={!payable.length}
              onRefresh={refresh}
              refreshing={refreshing}
            />
          }
        />
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Spin size="large" />
          </div>
        ) : isError ? (
          <EmptyScreen
            Icon={CloudServerOutlined}
            tone="brand"
            title={t("inventoryPage.payNeedsServerTitle")}
            text={t("inventoryPage.payNeedsServerText")}
          />
        ) : payable.length === 0 ? (
          <EmptyScreen Icon={CheckOutlined} title={t("inventoryPage.payAllPaid")} />
        ) : (
          <>
            <div
              ref={listRef}
              className={`flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto ${
                isNarrow ? "px-3 pb-6 pt-3.5" : "px-7 pb-8 pt-5"
              }`}
            >
              {groups.map((group) => {
                const ids = group.docs.map((doc) => doc.id);
                const count = ids.filter((id) => selected.has(id)).length;
                const all = count === ids.length;
                return (
                  <div key={group.key} className="shrink-0 overflow-hidden rounded-xl border border-shade-2 bg-panel">
                    <button
                      type="button"
                      data-pay-row
                      role="checkbox"
                      aria-checked={all ? true : count ? "mixed" : false}
                      onClick={() => toggle(ids, !all)}
                      className={`${rowClass} bg-fill`}
                    >
                      <CheckBox checked={all} indeterminate={!all && count > 0} />
                      <span className="min-w-0 flex-1 truncate text-[14px] font-semibold">{group.name}</span>
                      <span className="shrink-0 text-[12.5px] text-body">
                        {t("inventoryPage.payDocsCount", { count: group.docs.length })}
                      </span>
                      <span className="font-sheet font-semibold">{formatMoney(group.total)}</span>
                    </button>
                    {group.docs.map((doc) => {
                      const on = selected.has(doc.id);
                      const age = payableAgeDays(doc);
                      const overdue = age > OVERDUE_PAYMENT_DAYS;
                      const paidNotes = doc.paidNoteIds?.length ?? 0;
                      const secondary = [
                        dayjs(doc.date).format("DD.MM.YYYY"),
                        doc.warehouseName,
                        doc.createdByName ? t("inventoryPage.enteredBy", { name: doc.createdByName }) : null,
                        paidNotes ? t("inventoryPage.payNotesPaidSeparately", { count: paidNotes }) : null,
                        doc.note,
                      ]
                        .filter(Boolean)
                        .join(" · ");
                      return (
                        <button
                          key={doc.id}
                          type="button"
                          data-pay-row
                          role="checkbox"
                          aria-checked={on}
                          onClick={() => toggle([doc.id], !on)}
                          className={`${rowClass} border-t border-solid border-shade-1 ${
                            on ? "bg-brand-tint" : "bg-transparent hover:bg-fill"
                          }`}
                        >
                          <CheckBox checked={on} />
                          <DeliveryTypeBadge documentType={doc.documentType} />
                          <span className="shrink-0 font-medium">
                            {doc.invoiceNumber || t("inventoryPage.noDocNumber")}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-[12.5px] text-muted" title={secondary}>
                            {secondary}
                          </span>
                          <span
                            className={`shrink-0 text-[12.5px] ${overdue ? "font-medium text-danger-ink" : "text-body"}`}
                          >
                            {age <= 0 ? t("inventoryPage.postingAgeToday") : t("inventoryPage.payAge", { count: age })}
                          </span>
                          <span className="min-w-[90px] shrink-0 text-right font-sheet font-semibold">
                            {formatMoney(doc.totalValueWithVat || 0)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            <footer
              className={`flex shrink-0 flex-wrap items-center gap-4 border-t border-shade-2 bg-panel ${
                isNarrow
                  ? "px-3.5 py-3 pb-[calc(12px+env(safe-area-inset-bottom,0px)*0.5)]"
                  : "px-6 py-3.5"
              }`}
            >
              <span className="text-body">
                {t("inventoryPage.paySelected", { count: selectedDocs.length })}
              </span>
              <span className="font-sheet text-xl font-bold text-brand-deep-ink">
                {formatMoney(selectedTotal)}
              </span>
              <div className="flex-1" />
              <button
                type="button"
                onClick={openPay}
                className={`${PRIMARY_BUTTON_CLASS} !px-[22px] !py-3 ${
                  selectedDocs.length ? "" : "opacity-55"
                } ${isNarrow ? "flex-[1_1_100%]" : ""}`}
              >
                {t("inventoryPage.payMarkPaid")}
                {!isNarrow && <KbdChip>Ctrl+Enter</KbdChip>}
              </button>
            </footer>
          </>
        )}
        <PayDeliveriesModal
          open={payOpen}
          subtitle={`${t("inventoryPage.payDocsCount", { count: selectedDocs.length })} · ${formatMoney(selectedTotal)}`}
          saving={payMutation.isPending}
          onCancel={() => setPayOpen(false)}
          onConfirm={confirmPay}
        />
      </div>
    </ConfigProvider>
  );
};

export default DeliveryPaymentsPage;
